import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Loader2, AlertTriangle, ArrowRight } from 'lucide-react';
import Header from '../components/Header';
import { api } from '../lib/api';

const MAX_POLLS = 12;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [state, setState] = useState('checking');
  const [plan, setPlan] = useState(null);

  useEffect(() => {
    document.title = 'Payment — Trades & Neighbours';
    if (!sessionId) { setState('error'); return; }
    let polls = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.paymentStatus(sessionId);
        setPlan(data.plan_label);
        if (data.payment_status === 'paid') { setState('paid'); return; }
        if (['failed', 'expired'].includes(data.payment_status)) { setState('failed'); return; }
      } catch {
        /* keep polling — the record may not be visible yet */
      }
      polls += 1;
      if (polls >= MAX_POLLS) { setState('timeout'); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="payment-success-page">
      <Header />
      <div className="mx-auto max-w-2xl px-6 py-24 md:px-10">
        {state === 'checking' && (
          <div className="border border-[#E5E4E0] bg-white p-10" data-testid="payment-checking">
            <Loader2 className="animate-spin text-[#1A362D]" size={28} />
            <h1 className="mt-6 font-heading text-3xl font-bold text-[#1C1B1A]">Confirming your payment…</h1>
            <p className="mt-2 text-[#5C5B5A]">Hang tight, this usually takes a couple of seconds.</p>
          </div>
        )}

        {state === 'paid' && (
          <div className="border border-[#1A362D] bg-white p-10" data-testid="payment-paid">
            <CheckCircle2 className="text-[#1A362D]" size={32} />
            <h1 className="mt-6 font-heading text-4xl font-bold leading-tight text-[#1C1B1A]">
              You're in{plan ? ` — ${plan}` : ''}.
            </h1>
            <p className="mt-3 text-[#5C5B5A]">
              Your subscription is active and your receipt is on its way by email.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/portfolio" className="btn-primary inline-flex items-center gap-2" data-testid="go-portfolio-btn">
                Open Property Passport <ArrowRight size={16} />
              </Link>
              <Link to="/tradie" className="border border-[#1C1B1A] px-5 py-3 text-sm font-semibold text-[#1C1B1A] transition-colors hover:bg-[#1C1B1A] hover:text-white" data-testid="go-tradie-btn">
                Tradie dashboard
              </Link>
            </div>
          </div>
        )}

        {['failed', 'timeout', 'error'].includes(state) && (
          <div className="border border-[#D96C4E] bg-white p-10" data-testid="payment-problem">
            <AlertTriangle className="text-[#D96C4E]" size={30} />
            <h1 className="mt-6 font-heading text-3xl font-bold text-[#1C1B1A]">
              {state === 'timeout' ? 'Still processing' : 'That payment didn\u2019t go through'}
            </h1>
            <p className="mt-3 text-[#5C5B5A]">
              {state === 'timeout'
                ? 'Your bank is taking longer than usual. Nothing is lost — refresh this page in a minute.'
                : 'No money has been taken. You can try again with a different card.'}
            </p>
            <Link to="/agency/pricing" className="btn-primary mt-8 inline-block" data-testid="retry-payment-btn">
              Back to plans
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
